import React, { FC, useState } from 'react'

import { useFieldArray, useForm } from 'react-hook-form'
import { v4 as uuidV4 } from 'uuid'

import { DecorationTitle } from 'src/components/atoms'
import { SettingButtons } from 'src/components/molecules'
import { ExternalUserDetailBasicInfoContent } from 'src/components/organisms'

import { BaseYup, yup, yupResolver } from 'src/customYup'
import {
  useAuthority,
  useExternalUser,
  useGetExternalUser,
  useGetExternalUsers,
  useToast,
  useUser,
} from 'src/hooks'
import { isArraysAreEqual } from 'src/libs/helper'
import { EditExternalUserBasicInfoFormEntity, ExternalUserEntity } from 'src/types'

const schema: yup.SchemaOf<EditExternalUserBasicInfoFormEntity> = BaseYup.object({
  name: BaseYup.string(),
  keywords: BaseYup.array()
    .of(
      BaseYup.object({
        id: BaseYup.string().required(),
        value: BaseYup.string().required(),
      })
    )
    .optional(),
})

type Props = {
  externalUser: ExternalUserEntity
  className?: string
}

export const ExternalUserDetailBasicInfo: FC<Props> = ({ externalUser, className }) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const toDefaultValues = {
    name: externalUser.name ?? undefined,
    keywords:
      externalUser.keywords && externalUser.keywords.length > 0
        ? externalUser.keywords.map(keyword => ({ id: uuidV4(), value: keyword }))
        : [{ id: uuidV4(), value: '' }],
  }

  const { control, register, watch, reset } = useForm<EditExternalUserBasicInfoFormEntity>({
    resolver: yupResolver(schema),
    defaultValues: toDefaultValues,
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'keywords',
  })

  const { updateName } = useExternalUser()
  const { updateKeywords } = useUser()
  const { displayStandardTextToast } = useToast()
  const { refetch: refetchExternalUser } = useGetExternalUser({ userId: externalUser.id })
  const { refetch: refetchExternalUsers } = useGetExternalUsers()
  const { displayErrorTextToast } = useToast()

  const { canManageUser } = useAuthority()

  const handleFinishEdit = async () => {
    setIsUpdating(true)

    try {
      let updateFuncs: Array<Promise<any>> = []

      if (!!watch('name') && externalUser.name !== watch('name')) {
        const f = updateName({
          userId: externalUser.id,
          updatedName: watch('name') ?? externalUser.name ?? '',
        })
        updateFuncs = [f]
      }

      const beforeKeywords = externalUser.keywords ?? []
      const updatedKeywords = watch('keywords')?.map(keyword => keyword.value) ?? []
      // 変更前、変更後のキーワードが異なる場合のみ更新する
      if (!isArraysAreEqual(beforeKeywords, updatedKeywords)) {
        const f = updateKeywords({ userId: externalUser.id, updatedKeywords })
        updateFuncs = [...updateFuncs, f]
      }

      if (updateFuncs.length > 0) {
        await Promise.all(updateFuncs)
        await Promise.all([refetchExternalUser(), refetchExternalUsers()])
        displayStandardTextToast('変更しました')
      }
    } catch {
      displayErrorTextToast('ユーザー名の更新に失敗しました。')
    } finally {
      setIsUpdating(false)
      setIsEditMode(false)
    }
  }

  // 設定ページを押下した際にreact-hook-form内のキーワードが0件の場合は1件ある状態にする
  const handleOnClickSetting = () => {
    const watchedKeywords = watch('keywords')
    if (watchedKeywords && watchedKeywords.length === 0) {
      append({ id: uuidV4(), value: '' })
    }
  }

  const enabledCheckButton =
    (!!watch('name') || watch('keywords')?.some(keyword => !!keyword.value)) ?? false

  return (
    <div className={className}>
      <div className="tw-full-justify">
        <DecorationTitle>プロフィール</DecorationTitle>
        {canManageUser && (
          <SettingButtons
            isEditMode={isEditMode}
            isLoading={isUpdating}
            updateIsEditMode={setIsEditMode}
            enabledCheckButton={enabledCheckButton}
            finishEdit={handleFinishEdit}
            onClickCancel={() => reset(toDefaultValues)}
            onClickSetting={handleOnClickSetting}
          />
        )}
      </div>
      <ExternalUserDetailBasicInfoContent
        externalUser={externalUser}
        isEditMode={isEditMode}
        isUpdating={isUpdating}
        register={register}
        watch={watch}
        fields={fields}
        append={append}
        remove={remove}
        className="tw-mt-s2"
      />
    </div>
  )
}
